import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ITEM_H = 44;
const VISIBLE = 3;

function daysInMonth(month, year) {
  if (!month) return 31;
  return new Date(year || 2000, month, 0).getDate();
}

/* ─── Slot Wheel Column ─── */
function SlotColumn({ items, value, onChange, width, label }) {
  const ref = useRef(null);
  const isScrolling = useRef(false);

  const idx = items.indexOf(value);

  // Scroll to selected on mount / value change
  useEffect(() => {
    if (ref.current && !isScrolling.current) {
      const target = Math.max(0, idx) * ITEM_H;
      ref.current.scrollTo({ top: target, behavior: 'smooth' });
    }
  }, [idx]);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    isScrolling.current = true;
    clearTimeout(ref.current._snapTimer);
    ref.current._snapTimer = setTimeout(() => {
      const scrollTop = ref.current.scrollTop;
      const snapped = Math.round(scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(snapped, items.length - 1));
      ref.current.scrollTo({ top: clamped * ITEM_H, behavior: 'smooth' });
      if (items[clamped] !== value) onChange(items[clamped]);
      setTimeout(() => { isScrolling.current = false; }, 120);
    }, 80);
  }, [items, value, onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: width || 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA088', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{
        position: 'relative', height: ITEM_H * VISIBLE, overflow: 'hidden',
        borderRadius: 14, border: '2px solid #E4E1D2', background: '#fff', width: '100%',
      }}>
        {/* Highlight band */}
        <div style={{
          position: 'absolute', top: ITEM_H, left: 0, right: 0, height: ITEM_H,
          background: 'rgba(85,107,47,.08)', borderTop: '1.5px solid #C4CBB4', borderBottom: '1.5px solid #C4CBB4',
          pointerEvents: 'none', zIndex: 2,
        }} />
        {/* Fade top/bottom */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: ITEM_H, background: 'linear-gradient(180deg, rgba(255,255,255,.92), transparent)', pointerEvents: 'none', zIndex: 3 }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: ITEM_H, background: 'linear-gradient(0deg, rgba(255,255,255,.92), transparent)', pointerEvents: 'none', zIndex: 3 }} />
        {/* Scrollable items */}
        <div
          ref={ref}
          className="slot-scroll"
          onScroll={handleScroll}
          style={{
            height: '100%', overflowY: 'auto', scrollSnapType: 'y mandatory',
            paddingTop: ITEM_H, paddingBottom: ITEM_H,
          }}
        >
          {items.map((item, i) => {
            const selected = item === value;
            return (
              <div key={`${item}-${i}`} style={{
                height: ITEM_H, display: 'flex', alignItems: 'center', justifyContent: 'center',
                scrollSnapAlign: 'start', cursor: 'pointer', userSelect: 'none',
                fontWeight: selected ? 700 : 500, fontSize: selected ? 20 : 17,
                color: selected ? '#2C2F26' : '#B0B0A0',
                transition: 'color .12s, font-size .12s, font-weight .12s',
                fontFamily: "'Source Sans 3', sans-serif",
              }}
                onClick={() => {
                  onChange(item);
                  if (ref.current) ref.current.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
                }}
              >
                {label === 'Month' && typeof item === 'number' ? MONTHS[item - 1] : item}
              </div>
            );
          })}
        </div>
        <style>{`.slot-scroll { -ms-overflow-style: none; scrollbar-width: none; } .slot-scroll::-webkit-scrollbar { display: none; }`}</style>
      </div>
    </div>
  );
}

/* ─── DOB Slot Picker ─── */
function DobSlotPicker({ value, onChange, error }) {
  // Parse existing value (YYYY-MM-DD)
  const parts = value ? value.split('-') : [];
  const [year, setYear] = useState(parts[0] ? parseInt(parts[0]) : null);
  const [month, setMonth] = useState(parts[1] ? parseInt(parts[1]) : null);
  const [day, setDay] = useState(parts[2] ? parseInt(parts[2]) : null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const maxDay = daysInMonth(month, year);
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);

  // Clamp day if month/year changes
  useEffect(() => {
    if (day && day > maxDay) setDay(maxDay);
  }, [month, year, maxDay, day]);

  // Emit combined value
  useEffect(() => {
    if (year && month && day) {
      const m = String(month).padStart(2, '0');
      const d = String(Math.min(day, maxDay)).padStart(2, '0');
      onChange(`${year}-${m}-${d}`);
    }
  }, [year, month, day, maxDay, onChange]);

  return (
    <div>
      <label className="block text-base font-bold text-gray-700 mb-3">Date of Birth</label>
      <div style={{ display: 'flex', gap: 10 }}>
        <SlotColumn
          label="Month" width={1.3}
          items={months}
          value={month}
          onChange={setMonth}
        />
        <SlotColumn
          label="Day" width={0.8}
          items={days}
          value={day}
          onChange={setDay}
        />
        <SlotColumn
          label="Year" width={1}
          items={years}
          value={year}
          onChange={setYear}
        />
      </div>
      {/* Month name hint */}
      {month && (
        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 14, color: '#556B2F', fontWeight: 600 }}>
          {MONTHS[month - 1]} {day || '—'}, {year || '——'}
        </div>
      )}
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </div>
  );
}

function RegistrationFlow() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    gender: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Please select an option';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Store registration data and navigate to family setup
      sessionStorage.setItem('registrationData', JSON.stringify(formData));
      navigate('/auth/family-setup');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">Create Your Account</h1>
            <p className="text-base opacity-90">Join your family's story</p>
          </div>
          
          {/* Content */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="flex space-x-4">
                  <div className="w-1/2">
                    <label htmlFor="first_name" className="block text-base font-bold text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      placeholder="First Name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors ${
                        errors.first_name ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                      } focus:outline-none`}
                    />
                    {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="last_name" className="block text-base font-bold text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      placeholder="Last Name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors ${
                        errors.last_name ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                      } focus:outline-none`}
                    />
                    {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                  </div>
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-base font-bold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                  } focus:outline-none`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <DobSlotPicker
                value={formData.date_of_birth}
                onChange={(val) => {
                  setFormData(prev => ({ ...prev, date_of_birth: val }));
                  if (errors.date_of_birth) setErrors(prev => ({ ...prev, date_of_birth: '' }));
                }}
                error={errors.date_of_birth}
              />

              <div>
                <label className="block text-base font-bold text-gray-700 mb-2">
                  Gender
                </label>
                <div className="flex gap-3">
                  {[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'unknown', label: 'Prefer not to say' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, gender: opt.value }));
                        if (errors.gender) setErrors(prev => ({ ...prev, gender: '' }));
                      }}
                      className={`flex-1 py-3 px-3 text-base rounded-lg border-2 transition-colors ${
                        formData.gender === opt.value
                          ? 'border-[#556B2F] bg-[#556B2F] text-white font-semibold'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-[#556B2F]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-base font-bold text-gray-700 mb-2">
                  Create Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Create a secure password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors pr-12 ${
                      errors.password ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                    } focus:outline-none`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-base font-bold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-4 text-lg border-2 rounded-lg transition-colors pr-12 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300 focus:border-[#556B2F]'
                    } focus:outline-none`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>
              
              <button
                type="submit"
                className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23]"
              >
                Continue
              </button>
            </form>
            
            <p className="text-sm text-center text-gray-600 mt-4">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-[#556B2F] font-semibold hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationFlow; 